import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import * as THREE from "three";

const MEASURE_COLOR = 0xe53935;
const LABEL_SCALE_FACTOR = 0.22;

/**
 * Canvas-backed billboard sprite for dimension values.
 * @param {string} text
 * @param {number} worldScale
 */
function makeLabelSprite(text, worldScale) {
  const canvas = document.createElement("canvas");
  const size = 128;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#e53935";
  ctx.font = "bold 56px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(text), size / 2, size / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  });
  const sprite = new THREE.Sprite(material);
  const s = Math.max(worldScale * LABEL_SCALE_FACTOR, 2);
  sprite.scale.set(s, s, 1);
  sprite.userData.dispose = () => {
    texture.dispose();
    material.dispose();
  };
  return sprite;
}

/**
 * Dimension line with end ticks (world space).
 * @param {THREE.Vector3} start
 * @param {THREE.Vector3} end
 * @param {THREE.Vector3} tickDir unit vector for tick direction
 * @param {number} tickLen
 * @param {THREE.Material} material
 */
function makeDimensionLine(start, end, tickDir, tickLen, material) {
  const group = new THREE.Group();
  const pts = [start, end];
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  group.add(new THREE.Line(geo, material));

  const half = tickDir.clone().multiplyScalar(tickLen / 2);
  const tickStarts = [start, end];
  tickStarts.forEach((p) => {
    const a = p.clone().sub(half);
    const b = p.clone().add(half);
    const tGeo = new THREE.BufferGeometry().setFromPoints([a, b]);
    group.add(new THREE.Line(tGeo, material));
  });

  return group;
}

function disposeObject3D(obj) {
  const disposed = new Set();
  obj.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    const mats = child.material
      ? Array.isArray(child.material)
        ? child.material
        : [child.material]
      : [];
    mats.forEach((m) => {
      if (!disposed.has(m)) {
        disposed.add(m);
        m.dispose();
      }
    });
    if (child.userData?.dispose) child.userData.dispose();
  });
}

/**
 * Lightweight isometric Three.js preview of a single box (optional pallet base).
 * Axes: X = width, Y = height (up), Z = depth/length.
 *
 * @param {{
 *   label?: string,
 *   width?: number,
 *   height?: number,
 *   depth?: number,
 *   color?: number,
 *   baseHeight?: number,
 *   baseColor?: number,
 *   heightPx?: number,
 * }} props
 */
export default function DimensionBoxPreview({
  label,
  width = 0,
  height = 0,
  depth = 0,
  color = 0x5b8def,
  baseHeight = 0,
  baseColor = 0xc47a2c,
  heightPx = 160,
}) {
  const mountRef = useRef(null);
  const stateRef = useRef({
    renderer: null,
    scene: null,
    camera: null,
    contentGroup: null,
    animId: null,
    drag: { active: false, lastX: 0, lastY: 0, theta: Math.PI / 4, phi: Math.PI / 4 },
  });

  // Init scene once
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const w = mount.clientWidth || 200;
    const h = heightPx;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f7f5);

    const aspect = w / h;
    const frustum = 50;
    const camera = new THREE.OrthographicCamera(
      (-frustum * aspect) / 2,
      (frustum * aspect) / 2,
      frustum / 2,
      -frustum / 2,
      0.1,
      10000
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dir = new THREE.DirectionalLight(0xffffff, 0.5);
    dir.position.set(1, 2, 1);
    scene.add(dir);

    const contentGroup = new THREE.Group();
    scene.add(contentGroup);

    stateRef.current = {
      ...stateRef.current,
      renderer,
      scene,
      camera,
      contentGroup,
    };

    const updateCamera = (radius = 40, lookY = 0) => {
      const { drag } = stateRef.current;
      const { theta, phi } = drag;
      camera.position.set(
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.cos(phi),
        radius * Math.sin(phi) * Math.sin(theta)
      );
      camera.lookAt(0, lookY, 0);
      camera.updateProjectionMatrix();
    };
    updateCamera();

    const onPointerDown = (e) => {
      stateRef.current.drag.active = true;
      stateRef.current.drag.lastX = e.clientX;
      stateRef.current.drag.lastY = e.clientY;
    };
    const onPointerUp = () => {
      stateRef.current.drag.active = false;
    };
    const onPointerMove = (e) => {
      const d = stateRef.current.drag;
      if (!d.active) return;
      const dx = e.clientX - d.lastX;
      const dy = e.clientY - d.lastY;
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      d.theta -= dx * 0.01;
      d.phi = Math.max(0.15, Math.min(Math.PI / 2 - 0.05, d.phi + dy * 0.01));
      const maxDim = stateRef.current.maxDim || 40;
      const lookY = stateRef.current.lookY || 0;
      updateCamera(maxDim * 2.1, lookY);
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointermove", onPointerMove);

    const animate = () => {
      stateRef.current.animId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(stateRef.current.animId);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      disposeObject3D(contentGroup);
      renderer.dispose();
    };
  }, [heightPx]);

  // Update meshes / measure lines when dimensions change
  useEffect(() => {
    const { scene, camera, contentGroup } = stateRef.current;
    if (!scene || !camera || !contentGroup) return;

    while (contentGroup.children.length) {
      const child = contentGroup.children[0];
      contentGroup.remove(child);
      disposeObject3D(child);
    }

    const w = Math.max(0, Number(width) || 0);
    const h = Math.max(0, Number(height) || 0);
    const d = Math.max(0, Number(depth) || 0);

    if (w <= 0 || h <= 0 || d <= 0) return;

    const skidH = Math.max(0, Math.min(Number(baseHeight) || 0, h));
    const contentH = h - skidH;

    const addBox = (bw, bh, bd, yBottom, fillColor, edgeColor) => {
      if (bh <= 0) return;
      const geometry = new THREE.BoxGeometry(bw, bh, bd);
      const material = new THREE.MeshLambertMaterial({
        color: fillColor,
        transparent: true,
        opacity: 0.85,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(0, yBottom + bh / 2, 0);
      contentGroup.add(mesh);

      const edges = new THREE.EdgesGeometry(geometry);
      const line = new THREE.LineSegments(
        edges,
        new THREE.LineBasicMaterial({ color: edgeColor })
      );
      line.position.copy(mesh.position);
      contentGroup.add(line);
    };

    if (skidH > 0) {
      addBox(w, skidH, d, 0, baseColor, 0x8a4b12);
      if (contentH > 0) {
        addBox(w, contentH, d, skidH, color, 0x8a6a20);
      }
    } else {
      addBox(w, h, d, 0, color, 0x333333);
    }

    // Red measure lines + labels (outer envelope)
    const gap = Math.max(w, h, d) * 0.08;
    const tickLen = Math.max(w, h, d) * 0.04;
    const lineMat = new THREE.LineBasicMaterial({ color: MEASURE_COLOR });

    const hx = -w / 2 - gap;
    const hz = -d / 2;
    const heightStart = new THREE.Vector3(hx, 0, hz);
    const heightEnd = new THREE.Vector3(hx, h, hz);
    contentGroup.add(
      makeDimensionLine(
        heightStart,
        heightEnd,
        new THREE.Vector3(1, 0, 0),
        tickLen,
        lineMat
      )
    );
    const heightLabel = makeLabelSprite(formatDim(height), Math.max(w, h, d));
    heightLabel.position.set(hx - gap * 0.9, h / 2, hz);
    contentGroup.add(heightLabel);

    const wy = -gap;
    const wz = -d / 2 - gap;
    const widthStart = new THREE.Vector3(-w / 2, wy, wz);
    const widthEnd = new THREE.Vector3(w / 2, wy, wz);
    contentGroup.add(
      makeDimensionLine(
        widthStart,
        widthEnd,
        new THREE.Vector3(0, 0, 1),
        tickLen,
        lineMat
      )
    );
    const widthLabel = makeLabelSprite(formatDim(width), Math.max(w, h, d));
    widthLabel.position.set(0, wy - gap * 0.7, wz);
    contentGroup.add(widthLabel);

    const dx = w / 2 + gap;
    const dy = -gap;
    const depthStart = new THREE.Vector3(dx, dy, -d / 2);
    const depthEnd = new THREE.Vector3(dx, dy, d / 2);
    contentGroup.add(
      makeDimensionLine(
        depthStart,
        depthEnd,
        new THREE.Vector3(1, 0, 0),
        tickLen,
        lineMat
      )
    );
    const depthLabel = makeLabelSprite(formatDim(depth), Math.max(w, h, d));
    depthLabel.position.set(dx + gap * 0.7, dy - gap * 0.3, 0);
    contentGroup.add(depthLabel);

    const maxDim = Math.max(w, h, d);
    stateRef.current.maxDim = maxDim;
    stateRef.current.lookY = h / 2;
    const aspect =
      (mountRef.current?.clientWidth || 200) / (heightPx || 160);
    const frustum = maxDim * 1.85;
    camera.left = (-frustum * aspect) / 2;
    camera.right = (frustum * aspect) / 2;
    camera.top = frustum / 2;
    camera.bottom = -frustum / 2;

    const { drag } = stateRef.current;
    const radius = maxDim * 2.1;
    camera.position.set(
      radius * Math.sin(drag.phi) * Math.cos(drag.theta),
      radius * Math.cos(drag.phi),
      radius * Math.sin(drag.phi) * Math.sin(drag.theta)
    );
    camera.lookAt(0, h / 2, 0);
    camera.updateProjectionMatrix();
  }, [width, height, depth, color, baseHeight, baseColor, heightPx]);

  const hasDims =
    Number(width) > 0 && Number(height) > 0 && Number(depth) > 0;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#f5f7f5",
        height: heightPx + (label ? 28 : 0),
        display: "flex",
        flexDirection: "column",
      }}
    >
      {label && (
        <Typography
          variant="caption"
          sx={{ px: 1, pt: 0.5, fontWeight: 600, color: "text.secondary" }}
        >
          {label}
        </Typography>
      )}
      <Box
        ref={mountRef}
        sx={{
          flex: 1,
          minHeight: heightPx - (label ? 20 : 0),
          position: "relative",
          "& canvas": { display: "block", width: "100% !important" },
        }}
      >
        {!hasDims && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            Sin dimensiones
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function formatDim(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}
